import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { sessionsApi } from '../../utils/deviceSession';
import { generatePassword, validateNewPassword, authMessage } from '../../utils/passwords';

/**
 * Owner → someone else's password. Two ways:
 *  - Set new password: takes effect now and signs them out everywhere; the
 *    owner shares the new password with them.
 *  - Send reset email: they pick their own; works only if they can read
 *    that inbox. They stay signed in until they use the link.
 */
export default function PasswordDialog({ person, onClose }) {
  const { resetPassword } = useAuth();
  const [mode, setMode] = useState('set');
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { kind: 'set', password, devices } | { kind: 'email' }
  const [copied, setCopied] = useState(false);
  const [requireChange, setRequireChange] = useState(true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const setPassword = async () => {
    const invalid = validateNewPassword(pw);
    if (invalid) { setError(invalid); return; }
    setBusy(true); setError('');
    try {
      const r = await sessionsApi(auth.currentUser, { action: 'set-password', uid: person.uid, password: pw, requireChange }, { timeoutMs: 20000 });
      setDone({ kind: 'set', password: pw, devices: r.devices ?? 0, requireChange: !!r.requireChange });
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  const sendEmail = async () => {
    setBusy(true); setError('');
    try {
      await resetPassword(person.email);
      setDone({ kind: 'email' });
      // Audit trail only — the email already went out.
      sessionsApi(auth.currentUser, { action: 'log-event', type: 'password-reset-email', uid: person.uid }).catch(() => {});
    } catch (err) { setError(authMessage(err, 'Could not send the reset email.')); }
    setBusy(false);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(done.password); setCopied(true); } catch { /* select-and-copy by hand */ }
  };

  return (
    <div className="ses__modal" role="dialog" aria-modal="true" aria-label={`Password for ${person.name}`} onClick={() => !busy && onClose()}>
      <div className="ses__modal-box" onClick={(e) => e.stopPropagation()}>
        <h2 className="ses__modal-title">🔑 Password — {person.name}</h2>

        {done?.kind === 'set' && (
          <>
            <p className="ses__modal-body">
              Password changed. {person.name} was signed out of {done.devices} tracked device{done.devices === 1 ? '' : 's'} (and any others within the hour).
              Share the new password with them:
            </p>
            <div className="ses__pw-result">
              <code>{done.password}</code>
              <button type="button" className="ses__btn ses__btn--sm" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
            </div>
            <p className="ses__pw-hint">
              {done.requireChange
                ? 'This is a temporary password: right after signing in they’ll be asked to choose their own.'
                : 'They can change it themselves later under My Account → Change password.'}
            </p>
            <div className="ses__modal-actions"><button type="button" className="ses__btn" onClick={onClose}>Done</button></div>
          </>
        )}

        {done?.kind === 'email' && (
          <>
            <p className="ses__modal-body">
              Reset link sent to <b>{person.email}</b>. They stay signed in until they use it; once they set a new password, all their devices are signed out.
            </p>
            <div className="ses__modal-actions"><button type="button" className="ses__btn" onClick={onClose}>Done</button></div>
          </>
        )}

        {!done && (
          <>
            <div className="ses__seg" role="tablist">
              <button type="button" role="tab" aria-selected={mode === 'set'} className={`ses__seg-btn${mode === 'set' ? ' is-on' : ''}`} onClick={() => { setMode('set'); setError(''); }}>
                Set new password
              </button>
              <button type="button" role="tab" aria-selected={mode === 'email'} className={`ses__seg-btn${mode === 'email' ? ' is-on' : ''}`} onClick={() => { setMode('email'); setError(''); }}>
                Send reset email
              </button>
            </div>

            {error && <div className="ses__error">{error}</div>}

            {mode === 'set' ? (
              <>
                <p className="ses__modal-body">Takes effect now. {person.name} is signed out on every device and must sign in with the new password.</p>
                <label className="ses__field-label" htmlFor="ses-new-pw">New password</label>
                <div className="ses__pw-row">
                  <input
                    id="ses-new-pw"
                    className="ses__input"
                    type={show ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                  />
                  <button type="button" className="ses__btn ses__btn--sm" onClick={() => setShow((v) => !v)}>{show ? 'Hide' : 'Show'}</button>
                  <button type="button" className="ses__btn ses__btn--sm" onClick={() => { setPw(generatePassword()); setShow(true); setError(''); }}>Generate</button>
                </div>
                <label className="ses__check">
                  <input type="checkbox" checked={requireChange} onChange={(e) => setRequireChange(e.target.checked)} />
                  <span>Require {person.name} to choose a new password after signing in <em>(recommended)</em></span>
                </label>
                <div className="ses__modal-actions">
                  <button type="button" className="ses__btn" onClick={onClose} disabled={busy}>Cancel</button>
                  <button type="button" className="ses__btn ses__btn--danger-solid" onClick={setPassword} disabled={busy || !pw}>
                    {busy ? 'Saving…' : 'Set password & sign out'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="ses__modal-body">
                  {person.email
                    ? <>Emails a reset link to <b>{person.email}</b>. Only use this if that inbox is really theirs — otherwise set the password yourself.</>
                    : 'This account has no email address — set the password yourself instead.'}
                </p>
                <div className="ses__modal-actions">
                  <button type="button" className="ses__btn" onClick={onClose} disabled={busy}>Cancel</button>
                  <button type="button" className="ses__btn ses__btn--primary" onClick={sendEmail} disabled={busy || !person.email}>
                    {busy ? 'Sending…' : 'Send reset email'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
